import { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '@/context/AppContext';
import Stack from '@/shared/components/Stack/Stack';
import Button from '@/shared/components/Button/Button';
import Text from '@/shared/components/Text/Text';
import { Canvas } from '@react-three/fiber';
import { Grid } from '@react-three/drei';
import World from '../world/World';
import './Player.css';

const Player: FC = () => {
  const { state, setActiveWorld } = useAppContext();
  const navigate = useNavigate();
  const worlds = Object.values(state.worlds);
  const [currentWorldIndex, setCurrentWorldIndex] = useState(0);
  
  // Set the active world when the component mounts or when the current world index changes
  useEffect(() => {
    if (worlds.length > 0) {
      setActiveWorld(worlds[currentWorldIndex].id);
    }
  }, [currentWorldIndex, worlds, setActiveWorld]);

  // Handle navigation between worlds
  const handlePrevWorld = () => {
    if (currentWorldIndex > 0) {
      setCurrentWorldIndex(currentWorldIndex - 1);
    }
  };

  const handleNextWorld = () => {
    if (currentWorldIndex < worlds.length - 1) {
      setCurrentWorldIndex(currentWorldIndex + 1);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/');
  };

  // If there are no worlds, show a message
  if (worlds.length === 0) {
    return (
      <Stack className="player" direction="vertical" gap="md">
        <Text variant="h2">No worlds available</Text>
        <Button onClick={handleBackToDashboard}>Back to Dashboard</Button>
      </Stack>
    );
  }

  const currentWorld = worlds[currentWorldIndex];

  return (
    <Stack className="player" direction="vertical" gap="no">
      <div className="player__scene">
        <Canvas>
          <World world={currentWorld} transformMode="translate" />
        </Canvas>
        
        <div className="player__controls">
          <Button 
            onClick={handleBackToDashboard}
            className="player__back-button"
          >
            Back
          </Button>
          
          <div className="player__navigation">
            <Button 
              onClick={handlePrevWorld} 
              disabled={currentWorldIndex === 0}
            >
              Previous
            </Button>
            <Text variant="p">
              {currentWorldIndex + 1} / {worlds.length}
            </Text>
            <Button 
              onClick={handleNextWorld} 
              disabled={currentWorldIndex === worlds.length - 1}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </Stack>
  );
};

export default Player; 